import React, { useState, useEffect, useMemo } from 'react'
import { useTranslation } from '../../../shared/hooks/useIntractionTranslate'
import { useLocation, useNavigate } from 'react-router-dom'
import { Box, Stack, Typography, useMediaQuery } from '@mui/material'
import { ClipboardDocumentListIcon } from '@heroicons/react/24/outline'
import { INTERAX_COLORS } from '../../../constants/colors'
import { PageHeader } from '../../../shared/components/PageHeader'
import { OnboardingStepCardV2, OnboardingStepStatus, ArrowBox } from '../components/cards'
import { VideoModalV2 } from '../components/VideoModalV2'
import { AssessmentModal } from '../components/AssessmentModal'
import { DemographicQuestionsModal, type DemographicModalAnswers } from '../components/DemographicQuestionsModal'
import useAppState from '../../../hooks/useAppState'
import { useUpdateUser, useUser } from '../../../apiLayer'
import { OnboardingChapter } from '../../../../../pure-js/enums/OnboardingChapter'
import { isTutorialOnboardingStepCompleted } from '../../../../../pure-js/libs/gameTutorialProgress'
import { DemographicAnswers } from '../../../../../pure-js/types/Demographics'
import { DefaultModal, PageWrapper } from '../../../shared/components'
import {
  CONTINUE_FROM_COACH_GOALS_STATE,
  buildOnboardingStepDescriptors,
  type OnboardingStepKey,
  type OnboardingStepContext
} from './onboardingSteps.config.tsx'
import { ASSESSMENT_TOTAL_QUESTIONS, deriveAssessmentAnsweredCount } from '../../shared/utils/onboardingProgress'
import { useCoachStatus } from '../../../apiLayer/queryHooks/coach'
import { useIsCoachEnabled } from '../../../shared/hooks/useIsCoachEnabled'
import { GamerRoutes, SharedRoutes } from '../../../config/routes/routeEnums'
import { Button } from '../../../interaxDesignSystemComponents'
import { TESTIDS } from '../../../config/testids'
import { PlatformTourEntryButton } from '../platformTour/components/PlatformTourEntryButton'

/** The assessment modal's close animation, so the next thing does not open on top of it. */
const ASSESSMENT_MODAL_CLOSE_MS = 300 // 0.3 s

/**
 * Colours for a step pill in the progress row. Current step wins over completed, which wins over the
 * default — expressed as an ordered check rather than a chain of conditionals.
 */
function resolveStepPillColors(isCurrent: boolean, isCompleted: boolean): { background: string; border: string } {
  if (isCurrent) return { background: INTERAX_COLORS.sun, border: INTERAX_COLORS.sun }
  if (isCompleted) return { background: INTERAX_COLORS.green, border: INTERAX_COLORS.green }
  return { background: INTERAX_COLORS.heritageBlue40, border: INTERAX_COLORS.heritageBlue }
}

interface OnboardingGamerV2Props {
  className?: string
}

export const OnboardingGamerPage: React.FC<OnboardingGamerV2Props> = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { state, setState } = useAppState()

  const updateUserMutation = useUpdateUser()

  const isLargeScreen = useMediaQuery('(min-width:1000px)')

  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false)
  const [isGameVideoModalOpen, setIsGameVideoModalOpen] = useState(false)
  const [isAssessmentModalOpen, setIsAssessmentModalOpen] = useState(false)
  const [isDemographicsOpen, setIsDemographicsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<OnboardingStepKey>('intro')
  const [isCoachGoalsHandoverOpen, setIsCoachGoalsHandoverOpen] = useState(false)
  const [step1Completed, setStep1Completed] = useState(false)
  const [step2Completed, setStep2Completed] = useState(false)
  const [step3Completed, setStep3Completed] = useState(false)
  const [step4Completed, setStep4Completed] = useState(false)
  const [coachGoalsCompleted, setCoachGoalsCompleted] = useState(false)

  // The tutorial step is credited server-side from what the game reported, so it is read from the stored
  // user rather than the copy this session signed in with.
  const { data: userResponse } = useUser(state.user?.id ?? '', { staleTime: 0 })
  const storedUser = userResponse?.data

  const { data: coachStatus } = useCoachStatus()
  // Remembered per user: the goals card used to appear seconds after the page had finished loading.
  const coachEnabled = useIsCoachEnabled()

  // Read from the same ordering the cards are numbered from, so "next" can never disagree with the row.
  const nextStepAfterAssessment = useMemo(() => {
    const keys = buildOnboardingStepDescriptors(coachEnabled).map((descriptor) => descriptor.key)
    return keys[keys.indexOf('assessment') + 1]
  }, [coachEnabled])

  const answeredAssessmentQuestions = useMemo(() => {
    return deriveAssessmentAnsweredCount({
      currentQuestion: state.user?.currentAssessmentQuestion,
      completedSteps: state.user?.completedOnboardingSteps,
      totalQuestions: ASSESSMENT_TOTAL_QUESTIONS
    })
  }, [state.user?.completedOnboardingSteps, state.user?.currentAssessmentQuestion])

  useEffect(() => {
    const completedSteps = state.user?.completedOnboardingSteps || []
    setStep1Completed(completedSteps.includes(OnboardingChapter.Introduction))
    // Step 2 is only fully complete when BOTH demographics AND assessment are done
    setStep2Completed(
      completedSteps.includes(OnboardingChapter.Demographics) && completedSteps.includes(OnboardingChapter.Assessment)
    )
    setStep3Completed(completedSteps.includes(OnboardingChapter.GameIntroduction))
    // Completion is the game's word that the tutorial was played through, not that it was opened.
    setStep4Completed(
      isTutorialOnboardingStepCompleted({
        completedOnboardingSteps: storedUser?.completedOnboardingSteps ?? completedSteps,
        tutorialCompletedAt: storedUser?.tutorialCompletedAt ?? state.user?.tutorialCompletedAt
      })
    )
    // `state.user` is the copy loaded at sign-in, so it still says "not completed" after a goal is set
    // in this session. `coachStatus` is the server's answer and refetches when a conversation ends.
    setCoachGoalsCompleted(
      completedSteps.includes(OnboardingChapter.AiCoachGoals) ||
        Boolean(state.user?.coach?.onboardingGoalsCompletedAt) ||
        coachStatus?.onboardingGoalsCompleted === true
    )
  }, [
    state.user?.completedOnboardingSteps,
    state.user?.tutorialCompletedAt,
    storedUser?.completedOnboardingSteps,
    storedUser?.tutorialCompletedAt,
    state.user?.coach?.onboardingGoalsCompletedAt,
    coachStatus?.onboardingGoalsCompleted
  ])

  // Arriving from Aria with goals just set continues the journey: the game introduction opens by
  // itself while it is still outstanding, exactly as finishing a step on this page would open it.
  const location = useLocation()
  useEffect(() => {
    const arrivedFromCoachGoals = (location.state as Record<string, unknown> | null)?.[CONTINUE_FROM_COACH_GOALS_STATE]
    if (!arrivedFromCoachGoals) return
    // Consumed once — a refresh or back-navigation must not reopen the step.
    navigate(location.pathname, { replace: true, state: null })
    const completedSteps = state.user?.completedOnboardingSteps || []
    if (completedSteps.includes(OnboardingChapter.GameIntroduction)) return
    if (!isLargeScreen) setCurrentStep('game-intro')
    setTimeout(() => setIsGameVideoModalOpen(true), ASSESSMENT_MODAL_CLOSE_MS)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state])

  const persistOnboardingStep = async (chapter: OnboardingChapter) => {
    try {
      const currentCompletedSteps = state.user?.completedOnboardingSteps || []

      if (!currentCompletedSteps.includes(chapter)) {
        const updatedSteps = [...currentCompletedSteps, chapter]

        await updateUserMutation.mutateAsync({
          id: state.user.id,
          userData: {
            completedOnboardingSteps: updatedSteps
          }
        })

        setState({
          ...state,
          user: {
            ...state.user,
            completedOnboardingSteps: updatedSteps
          }
        })
      }
    } catch (_error) {
      // Continue even if save fails
    }
  }

  const handleVideoComplete = async () => {
    setStep1Completed(true)
    setIsVideoModalOpen(false)

    await persistOnboardingStep(OnboardingChapter.Introduction)

    setTimeout(() => {
      openAssessmentFlow()
      if (!isLargeScreen) {
        setCurrentStep('assessment')
      }
    }, 300)
  }

  const handleGameVideoComplete = async () => {
    setStep3Completed(true)
    setIsGameVideoModalOpen(false)

    await persistOnboardingStep(OnboardingChapter.GameIntroduction)

    setTimeout(() => {
      handleTutorialClick()
    }, 300)
  }

  /**
   * Opens the next incomplete part of the assessment flow:
   * 1. Demographics if not yet completed
   * 2. Otherwise the 37-question assessment
   */
  const openAssessmentFlow = () => {
    const completedSteps = state.user?.completedOnboardingSteps || []
    if (!completedSteps.includes(OnboardingChapter.Demographics)) {
      setIsDemographicsOpen(true)
    } else {
      setIsAssessmentModalOpen(true)
    }
  }

  /** Skip demographics — close without persisting, then open assessment */
  const handleDemographicsSkip = () => {
    setIsDemographicsOpen(false)
    setTimeout(() => setIsAssessmentModalOpen(true), 200)
  }

  /** Continue from demographics — persist chapter + consolidated answers to user doc, then open assessment */
  const handleDemographicsContinue = async (rawAnswers: DemographicModalAnswers) => {
    setIsDemographicsOpen(false)
    const completedSteps = state.user?.completedOnboardingSteps || []

    const now = Date.now()
    const demographics = Object.fromEntries(
      Object.entries(rawAnswers.answers)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [
          k,
          {
            answer: v as number,
            timeStamp: now,
            ...(k === 'demographic_role_level' && rawAnswers.roleLevelOtherText
              ? { otherText: rawAnswers.roleLevelOtherText }
              : {})
          }
        ])
    ) as DemographicAnswers

    const updatedSteps = completedSteps.includes(OnboardingChapter.Demographics)
      ? completedSteps
      : [...completedSteps, OnboardingChapter.Demographics]

    try {
      await updateUserMutation.mutateAsync({
        id: state.user.id,
        userData: { completedOnboardingSteps: updatedSteps, demographics }
      })
      setState({
        ...state,
        user: { ...state.user, completedOnboardingSteps: updatedSteps, demographics }
      })
    } catch (_error) {
      // Continue even if save fails — still open assessment so user can proceed
    }

    setTimeout(() => setIsAssessmentModalOpen(true), 200)
  }

  // Move on to whatever actually comes next: goal-setting sits between the assessment and the game
  // introduction when the coach is on, and lives on another page, so it is offered rather than opened.
  const advanceAfterAssessment = () => {
    if (nextStepAfterAssessment === 'coach-goals') {
      if (!isLargeScreen) setCurrentStep('coach-goals')
      setIsCoachGoalsHandoverOpen(true)
      return
    }

    if (!isLargeScreen) setCurrentStep('game-intro')
    setTimeout(() => setIsGameVideoModalOpen(true), ASSESSMENT_MODAL_CLOSE_MS)
  }

  const handleAssessmentComplete = async (_answers: Record<number, string>) => {
    setIsAssessmentModalOpen(false)
    try {
      const completedSteps = state.user?.completedOnboardingSteps || []
      if (completedSteps.includes(OnboardingChapter.Demographics)) {
        await persistOnboardingStep(OnboardingChapter.Assessment)
      }
    } catch (_error) {
      // A failed save must not strand them on a closed modal — they move on either way.
    }
    advanceAfterAssessment()
  }

  const handleTutorialClick = async () => {
    await startTutorialExperience()
  }

  const startTutorialExperience = async () => {
    navigate(`${GamerRoutes.BASE}${SharedRoutes.PLAY_GAME_WITH_INTRODUCTION}`)
  }

  const handleStepClick = (step: OnboardingStepKey) => {
    setCurrentStep(step)
  }

  const completionMap: OnboardingStepContext['completion'] = {
    intro: step1Completed,
    assessment: step2Completed,
    'coach-goals': coachGoalsCompleted,
    'game-intro': step3Completed,
    tutorial: step4Completed
  }

  const stepContext: OnboardingStepContext = {
    t,
    completion: completionMap,
    isLeadershipCoaching: coachStatus?.leadershipEnabled === true,
    progress: {
      assessment: {
        answeredQuestions: answeredAssessmentQuestions
      }
    },
    actions: {
      openIntroVideo: () => setIsVideoModalOpen(true),
      openAssessmentModal: () => openAssessmentFlow(),
      openCoachGoals: () => navigate(GamerRoutes.ARIA_COACH),
      openGameVideo: () => setIsGameVideoModalOpen(true),
      openTutorial: handleTutorialClick,
      startTutorial: startTutorialExperience
    }
  }

  const steps = buildOnboardingStepDescriptors(coachEnabled).map((descriptor) => ({
    key: descriptor.key,
    cardProps: descriptor.buildCardProps(stepContext)
  }))

  return (
    <PageWrapper>
      <PageHeader
        badgeText={t('gamer.onboarding.badge')}
        badgeIcon={<ClipboardDocumentListIcon />}
        title={t('gamer.onboarding.title')}
        subtitle={t('gamer.onboarding.subtitle')}
        actions={<PlatformTourEntryButton />}
      />

      {isLargeScreen ? (
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: '100%',
            maxWidth: '1440px',
            mx: 'auto',
            alignItems: 'stretch',
            position: 'relative'
          }}
        >
          {steps.map((step, index, array) => {
            const nextStep = array[index + 1]
            const arrowSize = 69
            const arrowOffset = arrowSize / 2 + 4

            return (
              <Box
                key={step.key}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  position: 'relative',
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                <OnboardingStepCardV2 {...step.cardProps} sx={{ width: '100%', maxWidth: '100%' }} />

                {nextStep && (
                  <ArrowBox
                    size={arrowSize}
                    ariaLabel={`Go to step ${nextStep.cardProps.stepNumber}`}
                    onClick={nextStep.cardProps.onAction}
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      right: `-${arrowOffset}px`,
                      transform: 'translateY(-50%)',
                      zIndex: 5
                    }}
                  />
                )}
              </Box>
            )
          })}
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              mb: 6
            }}
          >
            {steps.map((step) => {
              const isCompleted = step.cardProps.status === OnboardingStepStatus.COMPLETED
              const stepPillColors = resolveStepPillColors(currentStep === step.key, isCompleted)

              return (
                <Box
                  key={step.key}
                  onClick={() => handleStepClick(step.key)}
                  sx={{
                    px: 3,
                    py: 2,
                    borderRadius: '8px',
                    backgroundColor: stepPillColors.background,
                    border: `1px solid ${stepPillColors.border}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: currentStep === step.key ? INTERAX_COLORS.sun : INTERAX_COLORS.heritageBlue,
                      transform: 'translateY(-2px)'
                    }
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      fontSize: '14px',
                      fontWeight: 600,
                      color: INTERAX_COLORS.white
                    }}
                  >
                    {t('gamer.onboarding.stepLabel', { number: step.cardProps.stepNumber })}
                  </Box>
                </Box>
              )
            })}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center'
            }}
          >
            {steps
              .filter((step) => step.key === currentStep)
              .map((step) => (
                <OnboardingStepCardV2 key={step.key} {...step.cardProps} />
              ))}
          </Box>
        </>
      )}

      {/* Introduction Video Modal */}
      <VideoModalV2
        isOpen={isVideoModalOpen}
        onClose={() => setIsVideoModalOpen(false)}
        onComplete={handleVideoComplete}
        videoUrl="https://storage.googleapis.com/intraction-f177a.appspot.com/transition-web/assets/Intraction_Introduction.mp4"
        description={t('gamer.onboardingModal.subtitle')}
      />

      {/* Game Introduction Video Modal */}
      <VideoModalV2
        isOpen={isGameVideoModalOpen}
        onClose={() => setIsGameVideoModalOpen(false)}
        onComplete={handleGameVideoComplete}
        videoUrl="https://storage.googleapis.com/intraction-f177a.appspot.com/transition-web/assets/Intraction_Story.mp4"
        description={t('gamer.onboardingModal.gameSubtitle')}
      />

      {/* Demographics Modal (Step 1 of 2 in assessment flow) */}
      <DemographicQuestionsModal
        isOpen={isDemographicsOpen}
        onSkip={handleDemographicsSkip}
        onContinue={handleDemographicsContinue}
      />

      {/* Assessment Questions Modal (Step 2 of 2) */}
      <AssessmentModal
        isOpen={isAssessmentModalOpen}
        onClose={() => setIsAssessmentModalOpen(false)}
        onBack={() => {
          setIsAssessmentModalOpen(false)
          setTimeout(() => setIsDemographicsOpen(true), 200)
        }}
        onComplete={handleAssessmentComplete}
      />

      {/* Goal-setting happens on the coach page, so this step is offered rather than navigated into. */}
      <DefaultModal
        isOpen={isCoachGoalsHandoverOpen}
        onClose={() => setIsCoachGoalsHandoverOpen(false)}
        maxWidth="460px"
      >
        <Stack spacing={2} data-testid={TESTIDS.ONBOARDING_COACH_GOALS_HANDOVER}>
          <Typography sx={{ color: INTERAX_COLORS.white, fontWeight: 700, fontSize: 18 }}>
            {t('gamer.onboarding.coachGoalsHandover.title')}
          </Typography>
          <Typography sx={{ color: INTERAX_COLORS.white70, fontSize: 14 }}>
            {t('gamer.onboarding.coachGoalsHandover.body')}
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row', md: 'row' }} spacing={1.5} justifyContent="flex-end">
            <Button variant="secondary" size="small" onClick={() => setIsCoachGoalsHandoverOpen(false)}>
              {t('gamer.onboarding.coachGoalsHandover.later')}
            </Button>
            <Button
              variant="primary"
              size="small"
              onClick={() => {
                setIsCoachGoalsHandoverOpen(false)
                navigate(GamerRoutes.ARIA_COACH)
              }}
              data-testid={TESTIDS.ONBOARDING_COACH_GOALS_CONFIRM_BTN}
            >
              {t('gamer.onboarding.coachGoalsHandover.confirm')}
            </Button>
          </Stack>
        </Stack>
      </DefaultModal>
    </PageWrapper>
  )
}
